import { Icon as MaterialIcon, MenuItem } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { ActionPermissionsObject, IThemeStorageModel, IWorkspaceResourceTheme, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ThemesPaneController, { ThemesPaneControllerState } from "js/controllers/ThemesPaneController";
import WorkspaceController, { getPresentationsUsingSharedThemeCount, WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import themeManager from "js/core/themeManager";
import { pluralize } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { t } from "js/i18n";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMyResourcesThemeNotice } from "js/react/components/Notice";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { MultiSelectThumbnailGrid, ThumbnailGridSkeleton } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { Header, Layout, PaneBody, PaneContent, teamResourceGridProps } from "js/react/views/SharedResources/Components";
import {
    TeamResourceBadgeCluster,
    TeamResourceIconMenu,
    TeamResourceThumbnailActions,
    TeamResourceThumbnailCard,
    TeamResourceThumbnailImageWrap,
    TeamResourceUsageBadge,
} from "js/react/views/SharedResources/Components/TeamResourceThumbnailCard";

const logger = getLogger(LogGroup.TEAMS);

const SharedThemeThumbnailContainer = styled.div`
    min-height: 220px;
`;

const ThemeThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    min-width: 0;
    padding: 0;
    margin-top: 4px;
`;

const ThemeTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    min-height: 20px;
`;

const ThemeTitleText = styled.div`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0px;
    color: #444;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    flex: 1;
    min-width: 0;
    text-transform: none;
    margin: 0;
`;

const ThemePreviewWrap = styled(TeamResourceThumbnailImageWrap)`
    .thumbnail-container {
        gap: 0;
    }

    .thumbnail {
        border: none !important;
        box-shadow: none !important;
    }
`;

const StyledAppButton = styled(AppButton)`
    letter-spacing: 0.5px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
`;

function SharedThemeThumbnail(props: { resource: IWorkspaceResourceTheme, publishedCount: number, onVisibleChange: (visible: boolean, resource: IWorkspaceResourceTheme) => void }) {
    const { resource, publishedCount, onVisibleChange } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [theme, setTheme] = useState<IThemeStorageModel>(null);
    const [isHovering, setIsHovering] = useState(false);
    const [usageCount, setUsageCount] = useState(0);
    const visible = useIntersectionObserver(containerRef);

    useEffect(() => {
        if (!visible) {
            return;
        }

        themeManager.getSharedTheme(resource.resourceId, true)
            .then(setTheme)
            .catch(err => {
                logger.error(err, "[TeamSharedThemesPane] Error getting shared theme");
            });

        return () => {
            setTheme(null);
        };
    }, [resource.resourceId, visible]);

    useEffect(() => {
        onVisibleChange(visible, resource);
    }, [visible]);

    useEffect(() => {
        let isMounted = true;

        if (!visible) {
            return () => {
                isMounted = false;
            };
        }

        getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId })
            .then(count => {
                if (isMounted) {
                    setUsageCount(count);
                }
            })
            .catch(err => {
                logger.error(err, "[TeamSharedThemesPane] Error getting shared theme usage count", { themeId: resource.resourceId });
                if (isMounted) {
                    setUsageCount(0);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [resource.resourceId, visible]);

    const isDefault = resource.metadata.isDefault;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleEdit = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const shouldContinue = await ShowConfirmationDialog({
                title: t("Editing Shared Brand Kit"),
                message: `Any changes you make will impact ${pluralize(
                    "presentation",
                    teamThemeUsageCount,
                    "presentations"
                )} in your workspace.`,
                cancelButtonLabel: "Cancel",
                okButtonLabel: "Continue editing",
                optOutProperty: "editTeamThemeDialog"
            });

            if (!shouldContinue) {
                return;
            }
        }

        AppController.showThemeEditor({ themeId: resource.resourceId });
    };

    const handleRename = () => {
        ShowDialog(InputConfirmationDialog, {
            title: t("Rename Theme"),
            message: null,
            input: {
                value: resource.metadata.name
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: t("Renaming theme..."),
                        action: () => themeManager.updateThemeName(resource.resourceId, name.trim())
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: t("Unable to rename theme"),
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async () => {
        try {
            const theme = await themeManager.getSharedTheme(resource.resourceId, true);
            await withProgressDialog({
                title: t("Duplicating theme..."),
                action: async () => {
                    const name = t("Copy of {{name}}", { name: resource.metadata.name });
                    await themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: t("Unable to duplicate theme"),
                message: err.message
            });
        }
    };

    const handleDelete = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const confirmed = await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this Team Theme?"),
                message: t("Deleting this Team Theme permanently removes it from your Team Resources. Presentations currently using this theme will keep their existing design, but editors will be able to modify those themes without restriction."),
                okButtonLabel: t("Delete Team Theme"),
                buttonOptions: { acceptButtonColor: "red" }
            });

            if (!confirmed) {
                return;
            }
        }

        try {
            await withProgressDialog({
                title: t("Deleting theme..."),
                action: () => themeManager.deleteTheme(resource.resourceId)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: t("Unable to delete theme"),
                message: err.message
            });
        }
    };

    const handleToggleHide = async () => {
        try {
            await withProgressDialog({
                title: resource.metadata.isPublished ? t("Hiding theme...") : t("Unhiding theme..."),
                action: () => themeManager.updateThemeIsPublished(resource.resourceId, !resource.metadata.isPublished)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: t("Unable to toggle hide theme"),
                message: err.message
            });
        }
    };

    const canHide = resource.metadata.isPublished && publishedCount > 1;
    const showHideMenu = canHide || !resource.metadata.isPublished;
    const hoverActions = isHovering && (
        <TeamResourceThumbnailActions
            onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onMouseUp={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={event => event.stopPropagation()}
        >
            <TeamResourceIconMenu handleCloseMenu={() => setIsHovering(false)} fontSize="medium">
                {canUpgradeToManageSharedResources ? [
                    <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Theme...")}</MenuItem>,
                    <MenuItem key="rename" disabled divider><UpgradeIcon /><Gap5 />{t("Rename")}</MenuItem>,
                    <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                    <hr key="divider" />,
                    ...(showHideMenu ? [
                        resource.metadata.isPublished
                            ? <MenuItem key="hide" disabled divider><UpgradeIcon /><Gap5 />{t("Hide")}</MenuItem>
                            : <MenuItem key="unhide" disabled divider><UpgradeIcon /><Gap5 />{t("Show")}</MenuItem>
                    ] : [])
                ].filter(Boolean) : [
                    <MenuItem key="edit" onClick={() => handleEdit()}>
                        <MaterialIcon>edit</MaterialIcon>{t("Edit Theme...")}
                    </MenuItem>,
                    <MenuItem key="rename" onClick={() => handleRename()}>
                        <MaterialIcon>label</MaterialIcon>{t("Rename")}
                    </MenuItem>,
                    <MenuItem key="duplicate" onClick={() => handleDuplicate()}>
                        <MaterialIcon>content_copy</MaterialIcon>{t("Duplicate")}
                    </MenuItem>,
                    <hr key="divider" />,
                    ...(showHideMenu ? [
                        resource.metadata.isPublished ? (
                            <MenuItem key="hide" onClick={() => handleToggleHide()}>
                                <MaterialIcon>visibility_off</MaterialIcon>{t("Hide")}
                            </MenuItem>
                        ) : (
                            <MenuItem key="unhide" onClick={() => handleToggleHide()}>
                                <MaterialIcon>visibility_on</MaterialIcon>{t("Show")}
                            </MenuItem>
                        )
                    ] : [])
                ].filter(Boolean)}
                {!isDefault && (
                    canUpgradeToManageSharedResources ? (
                        <MenuItem key="delete" disabled divider><UpgradeIcon /><Gap5 />{t("Delete...")}</MenuItem>
                    ) : (
                        <MenuItem key="delete" onClick={() => handleDelete()}>
                            <MaterialIcon>delete</MaterialIcon>{t("Delete...")}
                        </MenuItem>
                    )
                )}
            </TeamResourceIconMenu>
        </TeamResourceThumbnailActions>
    );

    return (
        <SharedThemeThumbnailContainer ref={containerRef}>
            {!theme && <ThumbnailGridSkeleton />}
            {theme && (
                <TeamResourceThumbnailCard
                    data-item-id={resource.id}
                    selected={false}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    barVariant="custom"
                    barContent={(
                        <ThemeThumbnailContent>
                            <ThemeTitleRow>
                                <TruncatedTooltip title={resource.metadata.name} enterDelay={500} enterNextDelay={500}>
                                    <ThemeTitleText>{resource.metadata.name}</ThemeTitleText>
                                </TruncatedTooltip>
                                <TeamResourceBadgeCluster>
                                    <TeamResourceUsageBadge
                                        count={usageCount}
                                        showTitle
                                        title={t("Team Theme")}
                                        description={t("Only Librarians and Owners can edit. Changes apply everywhere this is used.")}
                                    />
                                </TeamResourceBadgeCluster>
                            </ThemeTitleRow>
                        </ThemeThumbnailContent>
                    )}
                >
                    <ThemePreviewWrap>
                        <ThemeThumbnail
                            item={theme}
                            resource={resource}
                            isDefault={isDefault}
                            isDowngraded={canUpgradeToManageSharedResources}
                            hideBar
                            themeThumbnailGrid
                            onDoubleClick={canUpgradeToManageSharedResources ? undefined : (() => handleEdit())}
                        />
                        {hoverActions}
                    </ThemePreviewWrap>
                </TeamResourceThumbnailCard>
            )}
        </SharedThemeThumbnailContainer>
    );
}

function TeamSharedThemesPane(props: WorkspaceControllerState & ThemesPaneControllerState) {
    const { fetching, data } = props;

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleCreate = () => {
        AppController.showThemeEditor({ themeId: "new-team-theme" });
    };

    const handleThumbnailVisibilityChange = (visible: boolean, resource: IWorkspaceResourceTheme) => {
        if (!visible) {
            return;
        }

        if (!ThemesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.resource.id === resource.id);
        if (index === data.length - 1) {
            ThemesPaneController.loadMore();
        }
    };

    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;
    // Count published themes in the loaded page only, not the workspace total from the API.
    // Pagination may not have fetched every published theme yet; using the API count could
    // show "Hide" on the only published theme in view while others are still on later pages.
    const publishedCount = data.filter(record => record.resource.metadata.isPublished).length;

    return (
        <Layout>
            {canManageSharedResources && (
                <Header title={t("Themes")}>
                    <StyledAppButton onClick={handleCreate} blue large>
                        <BaiIcon white fill>add_circle</BaiIcon>
                        {t("New Theme")}
                    </StyledAppButton>
                </Header>
            )}
            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Themes")}
                        bodyText={t("Upgrade your plan to keep colors, fonts, and logos consistent across presentations.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_themes" }
                            })
                        }
                    />
                </FlexBox>
            )}
            <PaneBody>
                <PaneContent>
                    <ResultsContainer>
                        <FetchingClickShield visible={isLoadingInitial} backgroundColor="#EEEEEE" />
                        {data.length === 0 && <NoMyResourcesThemeNotice />}
                        {data.length > 0 && (
                            <MultiSelectThumbnailGrid
                                {...teamResourceGridProps}
                                items={data}
                                allowMultiSelect={false}
                                allowDragging={false}
                                readOnly
                                isLoadingMore={isLoadingMore}
                                renderThumbnail={({ resource }: ThemesPaneControllerState["data"][number]) =>
                                    <SharedThemeThumbnail resource={resource} publishedCount={publishedCount} onVisibleChange={handleThumbnailVisibilityChange} />}
                            />
                        )}
                    </ResultsContainer>
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

function TeamSharedThemesPaneWrapper(props: WorkspaceControllerState & ThemesPaneControllerState) {
    useEffect(() => {
        const filters: ThemesPaneControllerState["filters"] = [
            {
                field: "resource.metadata.isShared",
                value: true,
                operator: "eq"
            },
        ];

        ThemesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            sort: [{ field: "resource.metadata.isDefault", direction: "desc" }, { field: "resource.modifiedAt", direction: "desc" }],
            filters
        });

        return () => {
            ThemesPaneController.reset();
        };
    }, []);

    if (!ThemesPaneController.initialized) {
        return <Layout><FetchingClickShield visible backgroundColor="#eee" /></Layout>;
    }

    return <TeamSharedThemesPane {...props} />;
}

export default WorkspaceController.withInitializedState(ThemesPaneController.withState(TeamSharedThemesPaneWrapper));
